import moment from "moment";
import { Link } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import Badge from "components/atoms/Badge/Badge";
import { IQuotation } from "pages/shipper/ShipmentQuotes/components/quotes/QuotesType";

interface IQuotationStatusProps {
  quotesData: IQuotation;
}

function QuotationStatus(props: IQuotationStatusProps) {
  const intl = useIntl();
  const { quotesData } = props;
  const { general, status } = quotesData || {};
  const isInvalidFreightMode = general?.is_invalid_freight_mode;

  return (
    <div>
      <div className="d-flex flex-column align-items-start">
        {general?.quote_expiration_seconds_remaining < 0 &&
          !["booked", "booked_as_duplicate", "canceled", "canceled_by_provider", "lost"].includes(status) &&
          !general.booked_as_duplicate_shipment_id &&
          !isInvalidFreightMode && (
            <>
              <Badge
                label={intl.formatMessage({
                  id: "general.expired",
                  defaultMessage: "Expired",
                })}
                type="secondary"
              />
              {general?.extension_requested_at && (
                <p className="mb-0 font-b9 text-warning mt-2">
                  <FormattedMessage id="quote.quoteRequestedExtension" defaultMessage="Shipper requested extension" />
                </p>
              )}
            </>
          )}
        {general?.quote_expiration_seconds_remaining > 0 && (
          <>
            {["confirmation_needed"].includes(status) && (
              <>
                <Badge
                  label={intl.formatMessage({
                    id: "tabs.confirmationRequired",
                    defaultMessage: "Confirmation required",
                  })}
                  type="warning"
                />
                <p className="mb-0">
                  <p className="d-flex align-items-center mt-1">
                    <span>
                      <FormattedMessage id="quote.quoteExpiresIn" defaultMessage="Quotes expires in" />
                    </span>
                    <span className="ml-2">{general.quote_expiration_remaining_time_display}</span>
                  </p>
                </p>
              </>
            )}
            {["submitted", "open_to_book", "require_approval"].includes(status) && (
              <>
                <Badge
                  label={intl.formatMessage({
                    id: "general.submitted",
                    defaultMessage: "Submitted",
                  })}
                  type="secondary"
                />
                <p className="mb-0">
                  <p className="d-flex align-items-center mt-1">
                    <span>
                      <FormattedMessage id="quote.quoteExpiresIn" defaultMessage="Quotes expires in" />
                    </span>
                    <span className="ml-2">{general.quote_expiration_remaining_time_display}</span>
                  </p>
                </p>
              </>
            )}
          </>
        )}
        {(status === "booked" || status === "booked_as_duplicate" || general.booked_as_duplicate_shipment_id) && (
          <>
            <Badge label={status === "booked" ? "Booked" : "Booked as duplicate"} type="primary" />
            <p className="mb-0">
              {general.booked_as_duplicate_date && (
                <p className="d-flex align-items-center mt-1 ml-2 mb-1">
                  <span>
                    <FormattedMessage id="quote.bookedOn" defaultMessage="Booked on" />
                  </span>
                  <span className="ml-2">{moment(general.booked_as_duplicate_date).format("DD MMM YYYY")}</span>
                </p>
              )}
              {general.booked_as_duplicate_shipment_id && (
                <span className="ml-2">
                  <Link to={`/shipment/${general.booked_as_duplicate_shipment_id}/tracking`}>
                    {general.booked_as_duplicate_shipment_id}
                  </Link>
                </span>
              )}
            </p>
          </>
        )}
        {(status === "canceled" || status === "canceled_by_provider") && !general.booked_as_duplicate_shipment_id && (
          <div className="d-flex">
            {!isInvalidFreightMode ? (
              <Badge
                label={intl.formatMessage({
                  id: "general.canceled",
                  defaultMessage: "Canceled",
                })}
                type="danger"
              />
            ) : (
              <Badge
                label={intl.formatMessage({
                  id: "shipment-quotes.components.quotes.invalidFreightMode",
                  defaultMessage: "Invalid freight mode",
                })}
                type="danger"
              />
            )}
          </div>
        )}
        {status === "lost" && (
          <div className="d-flex">
            <Badge
              label={intl.formatMessage({
                id: "general.archived",
                defaultMessage: "Archived",
              })}
              type="secondary"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default QuotationStatus;
